import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FileText, Eye, Pencil, ShareNetwork, WhatsappLogo, Copy, FilePdf, Upload, X, CookingPot, MagnifyingGlass, Camera, FileXls, Gear, Scales, Buildings, ArrowsClockwise, SortAscending } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import RecipeImportAI from "@/components/RecipeImportAI";
import ExportTemplateConfig from "@/components/ExportTemplateConfig";
import ExportSelectorDialog from "@/components/ExportSelectorDialog";
import { resolveImageUrl, formatCurrency, calcBakerPercent, NON_METRIC_UNITS } from "@/pages/sheetUtils";
import { IngredientCombobox, EstimatedPriceBadge } from "@/components/IngredientCombobox";

export default function TechnicalSheetForm(props) {
  const { t } = useLanguage();
  const { addMode, calculateGramaturaTotal, calculateRendimento, calculateTotalCost, customUnitWeight, dialogOpen, editingSheet, fatorCoccao, filteredIngredientsList, formData, handleAddIngredient, handleAddSubRecipe, handleCreateIngredientInline, handleDialogChange, handleImageUpload, handleRemoveImage, handleRemoveIngredient, handleSubmit, imagePreview, ingredientQuantity, ingredientSearch, ingredients, isQB, newIngForm, openEditIngredient, quantidadeBruta, recipeUnit, selectedIngredient, selectedSubRecipe, setAddMode, setCustomUnitWeight, setFatorCoccao, setFormData, setIngredientQuantity, setIngredientSearch, setIsQB, setNewIngForm, setQuantidadeBruta, setRecipeUnit, setSelectedIngredient, setSelectedSubRecipe, setShowNewIngredient, setSubRecipeQuantity, sheets, showNewIngredient, subRecipeQuantity, uploadingImage } = props;

  const CULINARY_UNITS = ["folha", "ramo", "dente", "maço", "pitada", "fatia", "colher_sopa", "colher_cha", "xicara", "caixa", "pacote", "lata", "garrafa", "bandeja"];
  return (
    <>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-sheet-btn"
              className="bg-zinc-950 text-white hover:bg-zinc-800"
            >
              <Plus size={18} className="mr-2" />
              {t("ts_new_sheet")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold tracking-tight">
                {editingSheet ? t("ts_edit_sheet") : t("ts_new_sheet")}
              </DialogTitle>
              <DialogDescription className="sr-only">{editingSheet ? "Editar ficha" : "Nova ficha"}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="product-name" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome do Produto</Label>
                  <Input
                    id="product-name"
                    data-testid="sheet-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Bolo de Chocolate"
                    required
                    className="mt-1.5"
                  />
                </div>
                
                <div>
                  <Label htmlFor="yield" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Rendimento</Label>
                  <Input
                    id="yield"
                    data-testid="sheet-yield-input"
                    type="number"
                    step="0.01"
                    value={formData.yield_quantity}
                    onChange={(e) => setFormData({ ...formData, yield_quantity: e.target.value })}
                    placeholder="12"
                    required
                    className="mt-1.5"
                  />
                </div>
                
                <div>
                  <Label htmlFor="yield-unit" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Unidade</Label>
                  <Select 
                    value={formData.yield_unit} 
                    onValueChange={(value) => setFormData({ ...formData, yield_unit: value })}
                  >
                    <SelectTrigger data-testid="sheet-yield-unit-select" className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unidades">unidades</SelectItem>
                      <SelectItem value="fatias">fatias</SelectItem>
                      <SelectItem value="porções">porções</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="g">g</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gramatura" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Gramatura por Porção (kg)</Label>
                  <Input
                    id="gramatura"
                    data-testid="sheet-gramatura-input"
                    type="number"
                    step="0.001"
                    value={formData.gramatura_porcao}
                    onChange={(e) => setFormData({ ...formData, gramatura_porcao: e.target.value })}
                    placeholder="Ex: 0.1"
                    className="mt-1.5"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Informe o peso de cada porção em kg</p>
                </div>
                
                {formData.ingredients.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                    <p className="text-xs uppercase tracking-wider text-blue-700 font-semibold">Cálculo Automático</p>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-blue-900">
                        <span className="font-semibold">Quantidade aproximada total:</span> <span className="font-mono">{calculateGramaturaTotal().toFixed(3)} kg</span>
                      </p>
                      {formData.gramatura_porcao && (
                        <>
                          <p className="text-sm text-blue-900">
                            <span className="font-semibold">Rendimento:</span> <span className="font-mono">{calculateRendimento()}</span> porções
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setFormData({ ...formData, yield_quantity: calculateRendimento() })}
                            className="mt-2 w-full bg-blue-600 hover:bg-blue-700 text-white text-xs"
                            data-testid="apply-calculated-yield"
                          >
                            Aplicar Rendimento Calculado
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Fórmula do Padeiro */}
              <div className="p-3 border border-zinc-200 rounded-lg bg-zinc-50">
                <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="formula-padeiro-toggle">
                  <input
                    type="checkbox"
                    checked={formData.formula_padeiro}
                    onChange={(e) => setFormData({ ...formData, formula_padeiro: e.target.checked })}
                    className="w-4 h-4 accent-zinc-900 rounded"
                  />
                  <div>
                    <span className="text-sm font-semibold text-zinc-900">Habilitar Fórmula do Padeiro</span>
                    <p className="text-[11px] text-zinc-500 mt-0.5">Calcula o % de cada ingrediente em relação ao ingrediente base (ex: farinha = 100%)</p>
                  </div>
                </label>
                {formData.formula_padeiro && formData.ingredients.filter(i => !i.is_qb).length > 0 && (
                  <div className="mt-3">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Ingrediente Base (100%)</Label>
                    <Select
                      value={formData.ingrediente_base_id}
                      onValueChange={(v) => setFormData({ ...formData, ingrediente_base_id: v })}
                    >
                      <SelectTrigger data-testid="select-ingrediente-base" className="mt-1.5">
                        <SelectValue placeholder="Selecione o ingrediente base..." />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.ingredients.filter(i => !i.is_qb).map((ing) => (
                          <SelectItem key={ing.ingredient_id} value={ing.ingredient_id}>
                            {ing.ingredient_name} ({ing.quantidade_bruta} {ing.unit})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="modo-preparo" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Modo de Preparo</Label>
                <textarea
                  id="modo-preparo"
                  data-testid="sheet-modo-preparo-input"
                  value={formData.modo_preparo}
                  onChange={(e) => setFormData({ ...formData, modo_preparo: e.target.value })}
                  placeholder="Descreva o modo de preparo..."
                  rows={4}
                  className="mt-1.5 w-full px-3 py-2 border border-zinc-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-transparent resize-none"
                />
              </div>
              
              {/* Upload de Foto */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                  Foto do Produto <span className="text-zinc-400 font-normal">(opcional)</span>
                </Label>
                
                {imagePreview || formData.foto_url ? (
                  <div className="mt-1.5 relative">
                    <img 
                      src={imagePreview || resolveImageUrl(formData.foto_url)} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-sm border border-zinc-200"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2"
                      data-testid="remove-image-btn"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1.5 flex gap-2">
                    <label className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-zinc-200 border-dashed rounded-sm cursor-pointer hover:bg-zinc-50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload size={28} className="text-zinc-400 mb-2" />
                        <p className="text-sm text-zinc-600 font-medium">
                          {uploadingImage ? "Enviando..." : "Galeria"}
                        </p>
                        <p className="text-xs text-zinc-400 mt-0.5">PNG, JPG, WEBP</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        data-testid="upload-image-input"
                      />
                    </label>
                    <label className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-emerald-200 border-dashed rounded-sm cursor-pointer hover:bg-emerald-50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Camera size={28} className="text-emerald-500 mb-2" />
                        <p className="text-sm text-emerald-700 font-medium">Câmera</p>
                        <p className="text-xs text-emerald-400 mt-0.5">Tirar foto</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        data-testid="camera-capture-input"
                      />
                    </label>
                  </div>
                )}
              </div>
              
              <div className="border-t border-zinc-200 pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-sm font-semibold text-zinc-900">Adicionar</h3>
                  <div className="flex bg-zinc-100 rounded-lg p-0.5">
                    <button type="button" onClick={() => setAddMode("ingredient")}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${addMode === "ingredient" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
                      data-testid="add-mode-ingredient">
                      Ingrediente
                    </button>
                    <button type="button" onClick={() => setAddMode("sub_recipe")}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${addMode === "sub_recipe" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}
                      data-testid="add-mode-sub-recipe">
                      Sub-receita
                    </button>
                  </div>
                </div>

                {addMode === "sub_recipe" ? (
                  <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">Use uma ficha técnica existente como ingrediente desta receita.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 items-end">
                      <div className="col-span-6">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Ficha Técnica</Label>
                        <div className="mt-1.5">
                          <IngredientCombobox
                            items={sheets.filter(s => s.id !== editingSheet?.id).map(s => ({ id: s.id, label: `${s.name} (${formatCurrency(s.cost_per_unit)}/${s.yield_unit})` }))}
                            value={selectedSubRecipe}
                            onSelect={setSelectedSubRecipe}
                            placeholder="Selecione a ficha..."
                            testId="select-sub-recipe"
                          />
                        </div>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                          Quantidade ({sheets.find(s => s.id === selectedSubRecipe)?.yield_unit || "un"})
                        </Label>
                        <Input type="number" step="0.001" value={subRecipeQuantity}
                          onChange={(e) => setSubRecipeQuantity(e.target.value)}
                          placeholder="Ex: 500" className="mt-1.5" data-testid="sub-recipe-quantity" />
                      </div>
                      <div className="col-span-3">
                        <Button type="button" onClick={handleAddSubRecipe}
                          className="w-full bg-blue-700 text-white hover:bg-blue-800" data-testid="add-sub-recipe-btn">
                          <Plus size={16} className="mr-1" /> Adicionar
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                <>
                <h3 className="text-sm font-semibold text-zinc-900 mb-3 sr-only">Adicionar Ingredientes</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 items-end mb-3">
                  <div className="col-span-10">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Ingrediente</Label>
                    <div className="mt-1.5 relative">
                      <Input
                        data-testid="ingredient-search-input"
                        value={ingredientSearch}
                        onChange={(e) => setIngredientSearch(e.target.value)}
                        placeholder="Buscar ingrediente..."
                        className="pr-8"
                      />
                      {ingredientSearch && (
                        <button
                          type="button"
                          onClick={() => { setIngredientSearch(""); setSelectedIngredient(""); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                        >✕</button>
                      )}
                    </div>
                    {ingredientSearch && (
                      <div className="mt-1 border border-zinc-200 rounded-lg max-h-40 overflow-y-auto bg-white shadow-lg z-10 relative">
                        {filteredIngredientsList.length === 0 ? (
                          <div className="p-3 text-xs text-zinc-500 text-center">
                            Nenhum encontrado. Cadastre um novo abaixo.
                          </div>
                        ) : (
                          filteredIngredientsList.map((ing) => (
                            <button
                              type="button"
                              key={ing.id}
                              onClick={() => {
                                setSelectedIngredient(ing.id);
                                setIngredientSearch(ing.name);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors border-b border-zinc-50 last:border-0 ${
                                selectedIngredient === ing.id ? 'bg-zinc-100 font-semibold' : ''
                              }`}
                            >
                              {ing.name}
                              <span className="text-zinc-400 ml-2 text-xs">
                                ({formatCurrency(ing.cost_per_unit)}/{ing.unit})
                              </span>
                              {ing.category && <span className="text-zinc-300 ml-1 text-[10px]">• {ing.category}</span>}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    {!ingredientSearch && !selectedIngredient && (
                      <div className="mt-1">
                        <IngredientCombobox
                          items={ingredients.map(ing => ({ id: ing.id, label: `${ing.name} (${formatCurrency(ing.cost_per_unit)}/${ing.unit})`, estimated: ing.is_estimated }))}
                          value={selectedIngredient}
                          onSelect={(v) => {
                            setSelectedIngredient(v);
                            const found = ingredients.find(i => i.id === v);
                            if (found) setIngredientSearch(found.name);
                          }}
                          placeholder="Ou selecione da lista..."
                          testId="select-ingredient"
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNewIngredient(!showNewIngredient)}
                      className="w-full h-10 text-xs"
                      data-testid="inline-new-ingredient-btn"
                    >
                      <Plus size={14} className="mr-1" />
                      Novo
                    </Button>
                  </div>
                </div>

                {/* Mini-formulário para novo ingrediente */}
                {showNewIngredient && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
                    <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Cadastrar Novo Ingrediente</p>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <Input
                          data-testid="new-ing-name"
                          placeholder="Nome *"
                          value={newIngForm.name}
                          onChange={(e) => setNewIngForm({ ...newIngForm, name: e.target.value })}
                          className="text-sm h-9 bg-white"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          data-testid="new-ing-qty"
                          type="number"
                          step="0.001"
                          placeholder="Qtd"
                          value={newIngForm.quantity_purchased}
                          onChange={(e) => setNewIngForm({ ...newIngForm, quantity_purchased: e.target.value })}
                          className="text-sm h-9 bg-white"
                        />
                      </div>
                      <div className="col-span-2">
                        <Select value={newIngForm.unit} onValueChange={(v) => setNewIngForm({ ...newIngForm, unit: v })}>
                          <SelectTrigger className="h-9 text-sm bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="g">g</SelectItem>
                            <SelectItem value="l">l</SelectItem>
                            <SelectItem value="ml">ml</SelectItem>
                            <SelectItem value="unidades">un</SelectItem>
                            <SelectItem value="maço">maço</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          data-testid="new-ing-price"
                          type="number"
                          step="0.01"
                          placeholder="R$ *"
                          value={newIngForm.purchase_price}
                          onChange={(e) => setNewIngForm({ ...newIngForm, purchase_price: e.target.value })}
                          className="text-sm h-9 bg-white"
                        />
                      </div>
                      <div className="col-span-2">
                        <Button
                          type="button"
                          onClick={handleCreateIngredientInline}
                          className="w-full h-9 bg-emerald-700 hover:bg-emerald-800 text-white text-xs"
                          data-testid="save-inline-ingredient"
                        >
                          Salvar
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 items-end">
                  <div className="col-span-3">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                      Qtd. Usada {!isQB && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      data-testid="ingredient-quantity-used"
                      type="number"
                      step="0.001"
                      value={isQB ? "" : ingredientQuantity}
                      onChange={(e) => {
                        setIngredientQuantity(e.target.value);
                        setQuantidadeBruta(e.target.value);
                      }}
                      placeholder={isQB ? "QB" : "Ex: 500"}
                      disabled={isQB}
                      className={`mt-1.5 ${isQB ? "bg-amber-50 border-amber-300" : ""}`}
                    />
                    {!isQB && (
                      <div className="flex gap-1 mt-1">
                        <select
                          data-testid="recipe-unit-select"
                          value={recipeUnit}
                          onChange={(e) => setRecipeUnit(e.target.value)}
                          className="text-[11px] border border-zinc-200 rounded px-1 py-0.5 text-zinc-600 bg-white max-w-[110px]"
                        >
                          <option value="">un. do insumo</option>
                          {CULINARY_UNITS.map((u) => <option key={u} value={u}>{u.replace("_", " ")}</option>)}
                        </select>
                        {recipeUnit && (
                          <input
                            data-testid="custom-unit-weight-input"
                            type="number" step="0.1" min="0"
                            value={customUnitWeight}
                            onChange={(e) => setCustomUnitWeight(e.target.value)}
                            placeholder="g/un (opc.)"
                            className="text-[11px] border border-zinc-200 rounded px-1 py-0.5 w-[76px]"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                      Qtd. Bruta
                    </Label>
                    <Input
                      data-testid="ingredient-qtd-bruta"
                      type="number"
                      step="0.001"
                      value={isQB ? "" : quantidadeBruta}
                      onChange={(e) => setQuantidadeBruta(e.target.value)}
                      placeholder={isQB ? "QB" : "Auto"}
                      disabled={isQB}
                      className={`mt-1.5 ${isQB ? "bg-amber-50 border-amber-300" : ""}`}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                      FC
                    </Label>
                    <Input
                      data-testid="ingredient-fc"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={isQB ? "" : fatorCoccao}
                      onChange={(e) => setFatorCoccao(e.target.value)}
                      placeholder={isQB ? "-" : "1"}
                      disabled={isQB}
                      className={`mt-1.5 ${isQB ? "bg-amber-50 border-amber-300" : ""}`}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                      Qtd. Líquida
                    </Label>
                    <Input
                      type="text"
                      value={isQB ? "" : (quantidadeBruta && fatorCoccao ? (parseFloat(quantidadeBruta) * parseFloat(fatorCoccao || 1)).toFixed(4) : "")}
                      disabled
                      placeholder={isQB ? "QB" : "Auto"}
                      className={`mt-1.5 ${isQB ? "bg-amber-50 border-amber-300" : "bg-zinc-50"}`}
                      data-testid="ingredient-qtd-liquida"
                    />
                  </div>

                  <div className="col-span-3 flex items-center justify-center">
                    <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors" data-testid="qb-toggle">
                      <input
                        type="checkbox"
                        checked={isQB}
                        onChange={(e) => setIsQB(e.target.checked)}
                        className="w-4 h-4 accent-amber-600 rounded"
                      />
                      <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">QB</span>
                      <span className="text-[10px] text-amber-600">(quanto bastar)</span>
                    </label>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 items-end mt-3">
                  <div className="col-span-12">
                    <Button
                      type="button"
                      data-testid="add-ingredient-to-sheet-btn"
                      onClick={handleAddIngredient}
                      className="w-full bg-zinc-950 text-white hover:bg-zinc-800"
                    >
                      <Plus size={18} className="mr-2" />
                      Adicionar Ingrediente
                    </Button>
                  </div>
                </div>
                </>
                )}
                
                {formData.ingredients.length > 0 && (
                  <div className="mt-4 border border-zinc-200 rounded-sm overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-zinc-50">
                          <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Ingrediente</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Qtd. Usada</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Qtd. Bruta</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">FC</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Qtd. Líquida</TableHead>
                          {formData.formula_padeiro && formData.ingrediente_base_id && (
                            <TableHead className="text-xs uppercase tracking-wider font-semibold text-amber-700">%</TableHead>
                          )}
                          <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Custo</TableHead>
                          <TableHead className="w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.ingredients.map((ing, index) => (
                          <TableRow key={ing.ingredient_id || index}>
                            <TableCell className="font-medium text-zinc-900">
                              {ing.ingredient_name}
                              {ing.is_qb && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">QB</span>}
                              {ing.is_sub_recipe && <span className="ml-2 text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">SUB</span>}
                              {!ing.is_sub_recipe && ingredients.find(c => c.id === ing.ingredient_id)?.is_estimated && <EstimatedPriceBadge className="ml-2" testId={`form-est-badge-${index}`} />}
                              {ing.peso_equivalente_g && !ing.is_sub_recipe && <span className="ml-1 text-[10px] text-blue-600">(~{ing.peso_equivalente_g}g/{ing.unit})</span>}
                            </TableCell>
                            <TableCell className="text-zinc-700">
                              {ing.is_qb ? <span className="text-amber-700 font-semibold text-xs">QB</span> : ing.recipe_unit ? <span className="font-mono text-emerald-800">{ing.recipe_quantity} {ing.recipe_unit.replace("_", " ")}{ing.recipe_quantity > 1 ? "s" : ""}</span> : <><span className="font-mono">{ing.quantity_used}</span> {ing.unit}</>}
                            </TableCell>
                            <TableCell className="text-zinc-700">
                              {ing.is_qb ? <span className="text-amber-700 font-semibold text-xs">QB</span> : <><span className="font-mono">{ing.quantidade_bruta}</span> {ing.unit}</>}
                            </TableCell>
                            <TableCell className="text-zinc-700 font-mono">
                              {ing.is_qb ? "-" : ing.fator_coccao.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-zinc-700">
                              {ing.is_qb ? <span className="text-amber-700 font-semibold text-xs">QB</span> : <><span className="font-mono">{ing.quantidade_liquida}</span> {ing.unit}</>}
                            </TableCell>
                            {formData.formula_padeiro && formData.ingrediente_base_id && (
                              <TableCell className="text-amber-700 font-mono font-semibold">
                                {ing.is_qb ? "-" : (
                                  ing.ingredient_id === formData.ingrediente_base_id
                                    ? "100%"
                                    : `${calcBakerPercent(ing, formData.ingredients, formData.ingrediente_base_id) || '-'}%`
                                )}
                              </TableCell>
                            )}
                            <TableCell className="text-right font-mono number text-zinc-900">
                              {ing.is_qb ? "-" : formatCurrency(ing.item_cost)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-0.5">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditIngredient(index)}
                                  title="Editar ingrediente"
                                  data-testid={`edit-ingredient-${index}`}
                                >
                                  <Pencil size={16} className="text-zinc-500" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveIngredient(index)}
                                  data-testid={`remove-ingredient-${index}`}
                                >
                                  <Trash size={16} className="text-rose-600" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-zinc-50 font-semibold">
                          <TableCell colSpan={formData.formula_padeiro && formData.ingrediente_base_id ? 6 : 5} className="text-zinc-900">Custo Total</TableCell>
                          <TableCell className="text-right font-mono number text-zinc-900" data-testid="total-cost-display">
                            {formatCurrency(calculateTotalCost())}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                        <TableRow className="bg-blue-50 font-semibold">
                          <TableCell colSpan={formData.formula_padeiro && formData.ingrediente_base_id ? 6 : 5} className="text-blue-900">Quantidade aproximada total</TableCell>
                          <TableCell className="text-right font-mono number text-blue-900" data-testid="total-gramatura-display">
                            {calculateGramaturaTotal().toFixed(3)} kg
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-zinc-200">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => handleDialogChange(false)}
                  data-testid="cancel-sheet-btn"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  data-testid="save-sheet-btn"
                  className="bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  {editingSheet ? "Atualizar Ficha" : "Salvar Ficha"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
    </>
  );
}
